import React from "react";
import { Check, Calendar, Repeat } from "lucide-react";

const PLAN_TILES = [
  {
    pkg: "one_time_99",
    badge: "ONE-OFF",
    title: "1-Week Content Pack",
    headline: "Get one week handled.",
    price: "$99.99",
    priceSub: "one-time payment",
    bullets: [
      "5 videos + 5 branded images, delivered in 72–96 hours",
      "Built for a single campaign, launch, or busy season",
      "No subscription. Pay once and you're done.",
    ],
  },
  {
    pkg: "monthly_175",
    title: "Monthly Content Engine",
    headline: "Become the business they remember.",
    price: "$175",
    priceSub: "per month · cancel anytime",
    valuePill: "20 ASSETS / MONTH",
    bullets: [
      "10 short-form videos + 10 branded images — every single month",
      "Your business shows up on Reels, TikTok & Instagram every week, on autopilot",
      "Buyers see you constantly → they trust you → they book YOU instead of the next guy",
      "Built around your brand. Refined every month as you grow.",
      "Cancel anytime — no retention calls, no contracts, no fees.",
    ],
    footnote: "Replaces a $1,500/mo social media manager — without the ghosting.",
    highlight: true,
  },
];

const Tile = ({ tile, selected, onClick }) => {
  const Icon = tile.highlight ? Repeat : Calendar;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`upsell-tile-${tile.pkg}`}
      className={[
        "snap-card text-left transition-all relative overflow-hidden flex flex-col",
        tile.highlight
          ? "bg-gradient-to-br from-[#0E1726] to-[#0B111D] border-[#3B9EFF]/40"
          : "",
        selected
          ? "border-[#3B9EFF] shadow-[0_0_0_2px_rgba(59,158,255,0.55),0_0_38px_rgba(59,158,255,0.28)]"
          : "hover:border-[#3B9EFF]/40",
      ].join(" ")}
    >
      {/* Top row: icon + best-value pill */}
      <div className="flex items-start justify-between gap-2">
        <span className="w-9 h-9 rounded-md bg-[#3B9EFF]/12 border border-[#3B9EFF]/35 flex items-center justify-center text-[#3B9EFF] shrink-0">
          <Icon size={18} />
        </span>
        {tile.highlight ? (
          <span className="font-accent text-[10px] tracking-widest text-[#3B9EFF] bg-[#3B9EFF]/12 border border-[#3B9EFF]/45 rounded-full px-2.5 py-1 whitespace-nowrap">
            BEST VALUE
          </span>
        ) : (
          <span className="font-accent text-[10px] tracking-widest text-[#A0AABF] bg-white/5 border border-white/10 rounded-full px-2.5 py-1 whitespace-nowrap">
            {tile.badge}
          </span>
        )}
      </div>

      {/* Selection state */}
      <span
        className={[
          "mt-4 font-accent text-[10px] tracking-widest",
          selected ? "text-[#3B9EFF]" : "text-[#A0AABF]",
        ].join(" ")}
      >
        {selected ? "● SELECTED" : "TAP TO SWITCH"}
      </span>

      {/* Title + headline */}
      <h3 className="mt-2 font-display font-bold text-white text-xl sm:text-2xl leading-tight tracking-tight">
        {tile.title}
      </h3>
      <p className="mt-2 font-display font-semibold text-white text-base sm:text-lg leading-snug">
        {tile.headline}
      </p>

      {/* Price — clear and upfront on both plans */}
      <div className="mt-4 flex items-baseline gap-2">
        <span
          className={[
            "font-display font-black tracking-tight text-3xl sm:text-4xl leading-none",
            tile.highlight ? "text-[#3B9EFF]" : "text-white",
          ].join(" ")}
        >
          {tile.price}
        </span>
        <span className="text-[#A0AABF] text-xs font-accent tracking-wider uppercase">
          {tile.priceSub}
        </span>
      </div>

      {/* Value pill (monthly only) */}
      {tile.valuePill && (
        <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-md bg-[#3B9EFF]/12 border border-[#3B9EFF]/40 px-3 py-1.5">
          <span className="font-display font-black text-[#3B9EFF] text-sm leading-none">
            {tile.valuePill}
          </span>
        </div>
      )}

      {/* Bullets */}
      <ul className="mt-4 space-y-2.5 flex-1">
        {tile.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[#D7DEEA] text-sm leading-relaxed">
            <Check size={14} className="text-[#3B9EFF] mt-0.5 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {tile.footnote && (
        <p className="mt-5 pt-4 border-t border-[#3B9EFF]/15 text-[#A0AABF] text-xs italic leading-relaxed">
          {tile.footnote}
        </p>
      )}
    </button>
  );
};

export const PlanUpsell = ({ selectedPkg, onSelect }) => {
  return (
    <div className="mt-7 max-w-2xl" data-testid="plan-upsell">
      <div className="flex items-center justify-between mb-3">
        <span className="font-accent text-[11px] tracking-widest text-[#3B9EFF]">
          PICK YOUR PLAN
        </span>
        <span className="text-[10px] text-[#A0AABF] font-accent tracking-widest">
          SAME CHECKOUT — TAP TO SWITCH
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLAN_TILES.map((tile) => (
          <Tile
            key={tile.pkg}
            tile={tile}
            selected={selectedPkg === tile.pkg}
            onClick={() => onSelect(tile.pkg)}
          />
        ))}
      </div>

      <p className="mt-4 text-xs text-[#A0AABF] leading-relaxed">
        Posting once and disappearing makes you forgettable.{" "}
        <span className="text-white font-semibold">
          Showing up every week is how local owners get booked.
        </span>{" "}
        Pick the plan that matches the result you actually want.
      </p>
    </div>
  );
};

export default PlanUpsell;
